import { mutate } from 'swr';
import { toastNotification } from '../../shared/toastNotification';
import {
  getEntry,
  deleteEntry,
  deleteManyEntries,
  putEntry,
  putManyEntry,
} from '../../services/api/entriesService';
import { balanceValuesActions } from '../../store/balanceValues';
import { entryValuesActions } from '../../store/entryValues';
import { dateFilterActions } from '../../store/dateFilter';
import { convertDate2 } from '../../helpers/dateConverter';
import {
  isEntryReconciled,
  isTransferReconciled,
  isTransferPairReconciled,
  hasReconciledEntries,
  canModifyEntry,
  hasEntriesInReconciledPeriod,
} from '../../helpers/reconciliationHelpers';

export const createActionHandlers = (
  dispatch,
  journalEntriesFilteredByDate,
  setEntryJournalOnFocus,
  banks = [], // Add banks parameter to check reconciliation dates
  setIsShownDialog,
  setIsShownCopyDialog,
  setIsShownTransferDialog,
  setIsShownInitialBalanceDialog,
  setIsShownTransferCopyDialog,
  setIsShownDeleteDialog,
  setIsShowDeleteManyDialog
) => {
  const openDialog = (journalEntry) => {
    dispatch(entryValuesActions.setEntryEdit(journalEntry));
    setEntryJournalOnFocus(journalEntry);
    setIsShownDialog(true);
  };

  const openCopyDialog = (journalEntry) => {
    setEntryJournalOnFocus(journalEntry);
    setIsShownCopyDialog(true);
  };

  const openTransferDialog = (journalEntry) => {
    dispatch(entryValuesActions.setTransferEdit(journalEntry));
    setEntryJournalOnFocus(journalEntry);
    setIsShownTransferDialog(true);
  };

  const openInitialBalanceDialog = (journalEntry) => {
    setEntryJournalOnFocus(journalEntry);
    setIsShownInitialBalanceDialog(true);
  };

  const openTransferCopyDialog = (journalEntry) => {
    if (!journalEntriesFilteredByDate && journalEntry.isInput === true) {
      journalEntriesFilteredByDate?.data.map((entry) => {
        if (journalEntry.journalEntryRefId === entry.id) {
          dispatch(entryValuesActions.setTransferCopy(journalEntry));
          setEntryJournalOnFocus(entry);
        }
      });
    } else {
      dispatch(entryValuesActions.setTransferCopy(journalEntry));
      setEntryJournalOnFocus(journalEntry);
    }
    setIsShownTransferCopyDialog(true);
  };

  const openDeleteDialog = (journalEntry) => {
    setEntryJournalOnFocus(journalEntry);
    setIsShownDeleteDialog(true);
  };

  const openDeleteManyDialog = (entryList) => {
    setEntryJournalOnFocus(entryList);
    setIsShowDeleteManyDialog(true);
  };

  const deleteMutation = async (entry, setIsShownDeleteDialog) => {
    let statusResponse = false;
    let toastContent = '';

    // Check if entry is reconciled
    let isReconciled = isEntryReconciled(entry);

    // For transfers, also check if the related transfer entry is reconciled
    if (entry.journalEntryType === 5 && !isReconciled) {
      try {
        let relatedEntry = null;

        if (entry.journalEntryRefId) {
          // This entry references another entry (destination entry)
          // Try to find it in current filtered data first
          relatedEntry = journalEntriesFilteredByDate?.data?.find(
            (e) => e.id === entry.journalEntryRefId
          );

          // If not found in filtered data, fetch it directly from API
          if (!relatedEntry) {
            const response = await getEntry(entry.journalEntryRefId);
            relatedEntry = response.data;
          }
        } else {
          // This entry is referenced by another entry (source entry)
          // Find the related entry by searching for entries that reference this one
          relatedEntry = journalEntriesFilteredByDate?.data?.find(
            (e) => e.journalEntryRefId === entry.id
          );

          // If not found in filtered data, we can't easily determine the related entry ID
          // without additional API calls. For now, we'll be conservative and assume
          // there might be a reconciled related entry if we can't find it
          if (!relatedEntry) {
            // We can't check the related entry, so err on the side of caution
            isReconciled = true;
          }
        }

        // Check if the related entry is reconciled
        if (relatedEntry && isEntryReconciled(relatedEntry)) {
          isReconciled = true;
        }
      } catch (error) {
        console.error('Error checking related transfer entry:', error);
        // If we can't check the related entry, err on the side of caution
        isReconciled = true;
      }
    }

    if (isReconciled) {
      toastNotification('warning', 'Não é possível excluir um lançamento conciliado.');
      return;
    }

    deleteEntry(entry.id)
      .then((res) => {
        statusResponse = 'success';
        toastContent = 'Lançamento excluído com sucesso.';
        if (setIsShownDeleteDialog) {
          setIsShownDeleteDialog(false);
        }
      })
      .catch((err) => {
        statusResponse = 'warning';
        toastContent = `Erro ao excluir lançamento.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        mutate('entriesFilteredByDate');
          mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
          mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
          mutate('banks');
      });
  };

  const deleteMany = async (entryList, setIsShowDeleteManyDialog) => {
    let statusResponse = false;
    let toastContent = '';

    if (entryList.some((entry) => entry.journalEntryType === 2)) {
      statusResponse = 'warning';
      toastContent = `Não é possível excluir lançamentos de saldo inicial.`;
      toastNotification(statusResponse, toastContent);
      return;
    }

    if (await checkForReconciledEntries(entryList)) {
      statusResponse = 'warning';
      toastContent = `Não é possível excluir lançamentos conciliados. Remova-os da seleção e tente novamente.`;
      toastNotification(statusResponse, toastContent);
      return;
    }

    const entryListIds = entryList.map((entry) => entry.id);
    deleteManyEntries(entryListIds)
      .then((res) => {
        statusResponse = 'success';
        toastContent = 'Lançamentos excluídos com sucesso.';
        if (setIsShowDeleteManyDialog) {
          setIsShowDeleteManyDialog(false);
        }
      })
      .catch((err) => {
        statusResponse = 'warning';
        toastContent = `Erro ao excluir lançamentos.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        mutate('entriesFilteredByDate');
          mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
          mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
          mutate('banks');
      });
  };

  const statusPaymentMutation = (entry) => {
    let statusResponse = false;
    let toastContent = '';

    if (isEntryReconciled(entry)) {
      statusResponse = 'warning';
      toastContent = `Não é possível alterar o status de lançamentos conciliados.`;
      toastNotification(statusResponse, toastContent);
      return;
    }

    // Check if entry is in reconciled period
    if (banks && Array.isArray(banks)) {
      const banksData = banks.data || banks;
      const bank = banksData.find((b) => b.id === entry.bankAccountId);

      if (bank) {
        const modifyCheck = canModifyEntry(entry, bank);
        if (!modifyCheck.canModify) {
          toastNotification('warning', modifyCheck.reason);
          return;
        }
      }
    }

    putEntry(entry)
      .then((res) => {
        statusResponse = 'success';
        toastContent = 'Lançamento atualizado com sucesso.';
      })
      .catch((err) => {
        statusResponse = 'warning';
        toastContent = `Erro ao atualizar lançamento.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        console.log('Invalidating cache after status change...');
        // Force complete cache invalidation to ensure dashboard updates
        mutate(() => true); // Invalidate ALL cache keys
        console.log('Invalidated all cache keys');
          mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
          mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
      });
  };

  const statusPaymentManyMutation = (entryList) => {
    let statusResponse = false;
    let toastContent = '';

    if (hasReconciledEntries(entryList)) {
      statusResponse = 'warning';
      toastContent = `Não é possível alterar o status de lançamentos conciliados.`;
      toastNotification(statusResponse, toastContent);
      return;
    }

    // Check if any entry is in reconciled period
    if (banks && Array.isArray(banks)) {
      const banksData = banks.data || banks;
      if (hasEntriesInReconciledPeriod(entryList, banksData)) {
        statusResponse = 'warning';
        toastContent = `Não é possível alterar o status de lançamentos com data dentro do período conciliado.`;
        toastNotification(statusResponse, toastContent);
        return;
      }
    }

    putManyEntry(entryList)
      .then((res) => {
        statusResponse = 'success';
        toastContent = 'Lançamentos atualizados com sucesso.';
      })
      .catch((err) => {
        statusResponse = 'warning';
        toastContent = `Erro ao atualizar lançamentos.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        console.log('Invalidating cache after bulk status change...');
        // Force complete cache invalidation to ensure dashboard updates
        mutate(() => true); // Invalidate ALL cache keys
        console.log('Invalidated all cache keys for bulk update');
        mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
        mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
      });
  };

  const checkForReconciledEntries = async (entryList) => {
    for (const entry of entryList) {
      // Check if entry itself is reconciled
      if (isEntryReconciled(entry)) {
        return true;
      }

      // For transfers, also check if the related transfer entry is reconciled
      if (entry.journalEntryType === 5) {
        try {
          let relatedEntry = null;

          if (entry.journalEntryRefId) {
            // This entry references another entry (destination entry)
            // Try to find it in current filtered data first
            relatedEntry = journalEntriesFilteredByDate?.data?.find(
              (e) => e.id === entry.journalEntryRefId
            );

            // If not found in filtered data, fetch it directly from API
            if (!relatedEntry) {
              const response = await getEntry(entry.journalEntryRefId);
              relatedEntry = response.data;
            }
          } else {
            // This entry is referenced by another entry (source entry)
            // Find the related entry by searching for entries that reference this one
            relatedEntry = journalEntriesFilteredByDate?.data?.find(
              (e) => e.journalEntryRefId === entry.id
            );

            // If not found in filtered data, we can't easily determine the related entry ID
            // without additional API calls. For now, we'll be conservative and assume
            // there might be a reconciled related entry if we can't find it
            if (!relatedEntry) {
              return true; // Err on the side of caution
            }
          }

          // Check if the related entry is reconciled
          if (relatedEntry && isEntryReconciled(relatedEntry)) {
            return true;
          }
        } catch (error) {
          console.error('Error checking related transfer entry:', error);
          return true; // Err on the side of caution
        }
      }
    }

    return false;
  };

  const checkEntries = (table) => {
    const hasPaidEntries = table
      .getFilteredSelectedRowModel()
      .flatRows.some((item) => item.original.statusPayment === true);
    const hasPendingEntries = table
      .getFilteredSelectedRowModel()
      .flatRows.some((item) => item.original.statusPayment === false);
    const hasInitialBalance = table
      .getFilteredSelectedRowModel()
      .flatRows.some(
        (item) => item.original.journalEntryType === 2 || item.original.journalEntryType === 5
      );
    return hasInitialBalance
      ? 'hasInitialBalance'
      : hasPaidEntries && hasPendingEntries
      ? 'mixed'
      : hasPaidEntries
      ? 'paid'
      : 'pending';
  };

  return {
    openDialog,
    openCopyDialog,
    openTransferDialog,
    openInitialBalanceDialog,
    openTransferCopyDialog,
    openDeleteDialog,
    openDeleteManyDialog,
    deleteMutation,
    deleteMany,
    checkForReconciledEntries,
    statusPaymentMutation,
    statusPaymentManyMutation,
    checkEntries,
  };
};
